import { getStockInsidersBasics } from "@/controllers/stock-insiders";
import { blackState, greyDarkerState, textColorState } from "@/lib/store";
import { classNames, nFormatter } from "@/lib/utils";
import { Stock } from "@/models/stock";
import { StockInsidersBasics } from "@/models/stock-insiders";
import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import IndicatorBar from "../indicator-bar/IndicatorBar";
import styles from "./StockInsiderMomentum.module.scss";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import Skeleton from "../skeleton/Skeleton";
import { brighten } from "@/lib/ortex-charts/color";

interface StockInsiderMomentumProps {
    stock: Stock;
    obfuscated?: boolean;
}

type periodValue = "1m" | "3m" | "6m" | "12m";

const StockInsiderMomentum = ({ stock, obfuscated }: StockInsiderMomentumProps) => {
    const black = useAtomValue(blackState);
    const greyDarker = useAtomValue(greyDarkerState);
    const whiteGrey = brighten(greyDarker, 0.59);
    const textColor = useAtomValue(textColorState);

    const [period, setPeriod] = useState<"1m" | "3m" | "6m" | "12m">("3m");
    const [basics, setBasics] = useState<StockInsidersBasics>();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // A failed or rate-limited call (#1348) leaves the box empty instead of reading a period that is not there.
        getStockInsidersBasics(stock.stock_id)
            .then((data) => setBasics(data))
            .catch(() => setBasics(undefined))
            .finally(() => setIsLoading(false));
    }, [stock.stock_id]);

    return isLoading ? (
        <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
    ) : (
        <>
            {basics?.[period] && (
                <div className={styles["basics-container"]}>
                    <ToggleButtonGroup exclusive value={period} onChange={obfuscated ? undefined : (event: React.SyntheticEvent, value: periodValue) => value && setPeriod(value)} aria-label="Insider momentum period selector" size="small">
                        <ToggleButton value="1m" style={{ color: textColor, minWidth: "25%" }}>
                            1m
                        </ToggleButton>
                        <ToggleButton value="3m" style={{ color: textColor, minWidth: "25%" }}>
                            3m
                        </ToggleButton>
                        <ToggleButton value="6m" style={{ color: textColor, minWidth: "25%" }}>
                            6m
                        </ToggleButton>
                        <ToggleButton value="12m" style={{ color: textColor, minWidth: "25%" }}>
                            12m
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <div className={styles.basics} style={{ height: "80%" }}>
                        <div className={classNames(styles.bar, obfuscated ? "obfuscated" : "")}>
                            <IndicatorBar title="Ortex Insider Momentum" percentage={basics[period].OIM_bar_position} />
                            <p className={styles["big-text"]}>{basics[period].OIM}</p>
                        </div>
                        <div className={styles.values} style={{ color: black }}>
                            <div className="bg-negative">
                                <p className={styles["small-text"]}>SELL</p>
                                <p className={classNames(styles["big-text"], styles.value, obfuscated ? "obfuscated" : "")}>
                                    {stock.currency} {nFormatter(basics[period].value_sold_local_raw, 2)}
                                </p>
                                <p className={styles["small-text"]}>Transactions</p>
                                <p className={classNames(styles["big-text"], obfuscated ? "obfuscated" : "")}>{basics[period].no_sells}</p>
                            </div>
                            <div className="bg-positive">
                                <p className={styles["small-text"]}>BUY</p>
                                <p className={classNames(styles["big-text"], styles.value, obfuscated ? "obfuscated" : "")}>
                                    {stock.currency} {nFormatter(basics[period].value_bought_local_raw, 2)}
                                </p>
                                <p className={styles["small-text"]}>Transactions</p>
                                <p className={classNames(styles["big-text"], obfuscated ? "obfuscated" : "")}>{basics[period].no_buys}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StockInsiderMomentum;
